import { contemTextoPesquisa } from "../Utils/pesquisaTexto";
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc
} from "firebase/firestore";
import { auth, db } from "../Config/firebase";
import Navbar from "../Components/Navbar/navbar";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import { consultaTenantAtual , aplicarTenantAtual} from "../Utils/tenant";

export default function CadastroConcreteiras({ open, isMobile }) {
  const [concreteiras, setConcreteiras] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [dialogAberto, setDialogAberto] = useState(false);
  const [concreteiraEditando, setConcreteiraEditando] = useState(null);
  const [nome, setNome] = useState("");
  const [pesquisa, setPesquisa] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("ativos");
  const [notificacao, setNotificacao] = useState({
    aberta: false,
    mensagem: "",
    tipo: "success"
  });

  async function carregarConcreteiras() {
    setCarregando(true);

    try {
      const snapshot = await getDocs(
        consultaTenantAtual(query(collection(db, "concreteiras"), orderBy("nome")))
      );
      setConcreteiras(
        snapshot.docs.map((documento) => ({
          id: documento.id,
          ...documento.data()
        }))
      );
    } catch (error) {
      console.error("Erro ao carregar concreteiras:", error);
      setNotificacao({
        aberta: true,
        mensagem: "Não foi possível carregar as concreteiras.",
        tipo: "error"
      });
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregarConcreteiras();
  }, []);

  const concreteirasFiltradas = useMemo(() => {
    const termo = pesquisa.trim().toLocaleLowerCase("pt-BR");

    return concreteiras.filter((concreteira) => {
      const correspondePesquisa = contemTextoPesquisa(String(concreteira.nome || "")
        .toLocaleLowerCase("pt-BR"), termo);
      const correspondeStatus =
        filtroStatus === "todos" ||
        (filtroStatus === "ativos" && concreteira.ativo !== false) ||
        (filtroStatus === "inativos" && concreteira.ativo === false);

      return correspondePesquisa && correspondeStatus;
    });
  }, [concreteiras, filtroStatus, pesquisa]);

  function abrirNovaConcreteira() {
    setConcreteiraEditando(null);
    setNome("");
    setDialogAberto(true);
  }

  function abrirEdicao(concreteira) {
    setConcreteiraEditando(concreteira);
    setNome(concreteira.nome || "");
    setDialogAberto(true);
  }

  async function salvarConcreteira() {
    const nomeNormalizado = nome.trim();

    if (!nomeNormalizado) {
      setNotificacao({
        aberta: true,
        mensagem: "Informe o nome da concreteira.",
        tipo: "warning"
      });
      return;
    }

    const duplicada = concreteiras.some(
      (concreteira) =>
        concreteira.id !== concreteiraEditando?.id &&
        String(concreteira.nome || "").trim().toLocaleLowerCase("pt-BR") ===
          nomeNormalizado.toLocaleLowerCase("pt-BR")
    );

    if (duplicada) {
      setNotificacao({
        aberta: true,
        mensagem: "Já existe uma concreteira com este nome.",
        tipo: "warning"
      });
      return;
    }

    setSalvando(true);

    try {
      if (concreteiraEditando) {
        await updateDoc(doc(db, "concreteiras", concreteiraEditando.id), {
          nome: nomeNormalizado,
          atualizadoEm: serverTimestamp(),
          atualizadoPor: auth.currentUser?.uid || null
        });
      } else {
        await addDoc(collection(db, "concreteiras"), aplicarTenantAtual({
          nome: nomeNormalizado,
          ativo: true,
          criadoEm: serverTimestamp(),
          criadoPor: auth.currentUser?.uid || null
        }));
      }

      setDialogAberto(false);
      setNotificacao({
        aberta: true,
        mensagem: concreteiraEditando
          ? "Concreteira atualizada com sucesso."
          : "Concreteira cadastrada com sucesso.",
        tipo: "success"
      });
      await carregarConcreteiras();
    } catch (error) {
      console.error("Erro ao salvar concreteira:", error);
      setNotificacao({
        aberta: true,
        mensagem: "Não foi possível salvar a concreteira.",
        tipo: "error"
      });
    } finally {
      setSalvando(false);
    }
  }

  async function alternarStatus(concreteira) {
    try {
      await updateDoc(doc(db, "concreteiras", concreteira.id), {
        ativo: concreteira.ativo === false,
        atualizadoEm: serverTimestamp(),
        atualizadoPor: auth.currentUser?.uid || null
      });
      await carregarConcreteiras();
    } catch (error) {
      console.error("Erro ao alterar status da concreteira:", error);
      setNotificacao({
        aberta: true,
        mensagem: "Não foi possível alterar o status.",
        tipo: "error"
      });
    }
  }

  return (
    <>
      <Navbar
        open={open}
        isMobile={isMobile}
        userName={auth.currentUser?.displayName || auth.currentUser?.email || ""}
      />

      <Box sx={{ p: { xs: 2, md: 4 }, mt: 6 }}>
        <Paper className="cabecalho-lista-cadastro" elevation={0}>
        <Typography variant="h5" mb={3}>
          Cadastro de Concreteiras
        </Typography>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          mb={3}
          alignItems={{ md: "center" }}
        >
          <Button variant="contained" onClick={abrirNovaConcreteira} sx={{ height: 56 }}>
            + Cadastrar Concreteira
          </Button>
          <TextField
            label="Pesquisar concreteira"
            value={pesquisa}
            onChange={(event) => setPesquisa(event.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField
            select
            label="Status"
            value={filtroStatus}
            onChange={(event) => setFiltroStatus(event.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="todos">Todos</MenuItem>
            <MenuItem value="ativos">Ativos</MenuItem>
            <MenuItem value="inativos">Inativos</MenuItem>
          </TextField>
        </Stack>
        </Paper>

        <Paper sx={{ overflowX: "auto" }}>
          <Table className="lista-cadastro-padrao">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Status</TableCell>
                <TableCell width={220}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {carregando && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!carregando && concreteirasFiltradas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>Nenhuma concreteira encontrada.</TableCell>
                </TableRow>
              )}
              {concreteirasFiltradas.map((concreteira) => (
                <TableRow key={concreteira.id}>
                  <TableCell className="lista-dado-principal">{concreteira.nome}</TableCell>
                  <TableCell>
                    {concreteira.ativo === false ? "Inativa" : "Ativa"}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => abrirEdicao(concreteira)}
                      >
                        Editar
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color={concreteira.ativo === false ? "success" : "error"}
                        onClick={() => alternarStatus(concreteira)}
                      >
                        {concreteira.ativo === false ? "Ativar" : "Desativar"}
                      </Button>
                      <HistoricoCadastro colecao="concreteiras" documentoId={concreteira.id} registro={concreteira} />
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      <Dialog
        open={dialogAberto}
        onClose={() => !salvando && setDialogAberto(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {concreteiraEditando ? "Editar Concreteira" : "Nova Concreteira"}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            label="Nome da concreteira"
            value={nome}
            onChange={(event) => setNome(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") salvarConcreteira();
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogAberto(false)} disabled={salvando}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={salvarConcreteira} disabled={salvando}>
            {salvando ? <CircularProgress size={22} /> : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notificacao.aberta}
        autoHideDuration={5000}
        onClose={() => setNotificacao((atual) => ({ ...atual, aberta: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={notificacao.tipo}
          variant="filled"
          onClose={() => setNotificacao((atual) => ({ ...atual, aberta: false }))}
        >
          {notificacao.mensagem}
        </Alert>
      </Snackbar>
    </>
  );
}
